<script setup>
import { requiredValidator } from '@/helpers/validators';
import ImageUploader from '@/components/common/ImageUploader.vue';
import i18n from '@/plugins/i18n';
import { useAccountStore } from '@/stores/account';
import { useRoleStore } from '@/stores/role';
import { useSnackBarStore } from '@/stores/snackbar';
import { ref } from 'vue';
import BaseDialog from '../widgets/BaseDialog.vue';
import { useConfigStore } from '@/stores/config';

const props = defineProps({
  openAccountDialog: {
    type: Boolean,
    required: false,
    default: false,
  },
  accountTitle: {
    type: String,
    required: true,
    default: '',
  },
  accountType: {
    type: String,
    required: true,
    default: '',
  },
  selectedAccount: {
    type: Object,
    required: true,
    default: () => {},
  },
  locales: {
    type: Array,
    required: false,
    default: () => [],
  },
  countries: {
    type: Array,
    required: false,
    default: () => [],
  },
  tenants: {
    type: Array,
    required: false,
    default: () => [],
  },
});

const emit = defineEmits(['save', 'close']);

const isPasswordVisible = ref(false);

const { t } = i18n.global;

const notification = useSnackBarStore();
const account = useAccountStore();
const roleStore = useRoleStore();
const config = useConfigStore();

const roles = ref([]);

const selectedAccount = ref({ account: { avatar: {}, is_active: true } });
const openAccountDialog = ref(false);

const saveLoading = ref(false);
const rolesLoading = ref(false);

const valid = ref(false);
const createAccountForm = ref();

const countryList = ref(props.countries);

onMounted(() => {
  if (props.accountType == 'admin') {
    getRoles();
  }
  if (!countryList?.length) {
    countryList.value = config.getConfigByKey('countries');
  }
});

async function getRoles() {
  rolesLoading.value = true;
  await roleStore
    .all()
    .then(resp => {
      roles.value = resp.data.data;
    })
    .finally(() => {
      rolesLoading.value = false;
    });
}

const syncAccountImage = images => {
  selectedAccount.value.account.avatar = images?.length > 0 ? images[0] : null;
};

const saveAccountInfo = async () => {
  const isValid = await createAccountForm.value.validate();
  if (!isValid.valid) {
    return;
  }

  const form = new FormData();

  form.set('first_name', selectedAccount.value.account.first_name);
  form.set('last_name', selectedAccount.value.account.last_name);
  form.set('email', selectedAccount.value.account.email);
  form.set('phone', selectedAccount.value.account.phone);
  form.set('country_id', selectedAccount.value.account.country_id);
  form.set('address', selectedAccount.value.account.address);
  form.set('country_code', selectedAccount.value.account.country_code);

  if (selectedAccount.value.account?.avatar?.id) {
    form.set('remove_image', 0);
    form.set('avatar_id', selectedAccount.value.account.avatar.id);
  } else {
    if (selectedAccount.value.account.id) {
      form.set('remove_image', 1);
    }
    if (selectedAccount.value.account.avatar) {
      form.set('avatar', selectedAccount.value.account.avatar);
    }
  }

  if (props.accountType != 'profile') {
    if (selectedAccount.value.account.password) {
      form.set('password', selectedAccount.value.account.password);
    }
    form.set('is_active', selectedAccount.value.account.is_active ? 1 : 0);
  }

  if (props.accountType == 'admin') {
    form.set('role_id', selectedAccount.value.role_id);
  } else if (props.accountType != 'profile') {
    form.set('locale_id', selectedAccount.value.locale_id);
    for (let i = 0; i < selectedAccount.value.tenant_ids.length; i++) {
      form.set('tenant_ids[' + i + ']', selectedAccount.value.tenant_ids[i]);
    }
  }

  let apiCall;
  let accountId = selectedAccount.value.account.id;
  if (accountId) {
    apiCall = account.edit(form, props.accountType, accountId, selectedAccount.value.account.uuid);
  } else {
    apiCall = account.create(form, props.accountType);
  }

  saveLoading.value = true;
  await apiCall
    .then(response => {
      openAccountDialog.value = false;
      emit('save', response.data.data);

      selectedAccount.value = {
        account: { avatar: null, is_active: true },
      };
    })
    .catch(notification.responseError)
    .finally(() => {
      saveLoading.value = false;
    });
};

watch(props, val => {
  openAccountDialog.value = structuredClone(toRaw(props.openAccountDialog));
});
watch(props, val => {
  selectedAccount.value = props.selectedAccount?.id
    ? props.selectedAccount
    : { locale_id: 1, account: { country_id: 121, avatar: null, is_active: true } };
});
</script>

<template>
  <BaseDialog
    v-if="openAccountDialog"
    :max-width="800"
    :title="props.accountTitle"
    :loading="saveLoading"
    @close="emit('close')"
    @save="saveAccountInfo">
    <template #content>
      <VForm ref="createAccountForm" v-model="valid">
        <VRow>
          <VCol cols="12">
            <h3>{{ $t('generalInformation') }}</h3>
          </VCol>
        </VRow>
        <VRow dense class="mt-2">
          <VCol cols="12" sm="4" justify="center" class="pr-2">
            <ImageUploader
              :images="[selectedAccount.account.avatar]"
              :subtitle="t('preferredSizeNoBackground')"
              :image-width="180"
              @sync-images="syncAccountImage" />
            <VSwitch
              v-if="props.accountType != 'profile'"
              v-model="selectedAccount.account.is_active"
              :label="selectedAccount.account.is_active ? $t('activatedAccount') : $t('deactivatedAccount')" />
          </VCol>
          <VCol cols="12" sm="8">
            <VRow dense>
              <VCol cols="12" sm="6">
                <VLabel> {{ $t('firstName') }}* </VLabel>
                <VTextField v-model="selectedAccount.account.first_name" variant="outlined" :rules="[requiredValidator]" density="compact" />
              </VCol>
              <VCol cols="12" sm="6">
                <VLabel> {{ $t('lastName') }}* </VLabel>
                <VTextField v-model="selectedAccount.account.last_name" variant="outlined" :rules="[requiredValidator]" density="compact" />
              </VCol>
              <VCol cols="12" sm="6">
                <VLabel>
                  {{ $t('countryCode') }}
                </VLabel>
                <VTextField v-model="selectedAccount.account.country_code" variant="outlined" density="compact" />
              </VCol>
              <VCol cols="12" sm="6">
                <VLabel>
                  {{ $t('phoneNumber') }}
                </VLabel>
                <VTextField v-model="selectedAccount.account.phone" variant="outlined" density="compact" />
              </VCol>
              <VCol cols="12" sm="6">
                <VLabel> {{ $t('emailAddress') }}* </VLabel>
                <VTextField
                  v-model="selectedAccount.account.email"
                  name="email_address"
                  variant="outlined"
                  :rules="[requiredValidator]"
                  density="compact" />
              </VCol>
              <VCol v-if="props.accountType != 'profile'" cols="12" sm="6">
                <VLabel> {{ $t('password') }} <span v-if="!selectedAccount.account.id">*</span> </VLabel>
                <VTextField
                  v-model="selectedAccount.account.password"
                  variant="outlined"
                  :rules="!selectedAccount.account.id ? [requiredValidator] : []"
                  density="compact"
                  :placeholder="t('keepItEmptyToDontChangeit')"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  :append-inner-icon="isPasswordVisible ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible" />
              </VCol>
            </VRow>
          </VCol>
        </VRow>
        <VRow>
          <VCol v-if="props.accountType == 'user'" cols="6" sm="4">
            <VLabel>
              {{ $t('language') }}
            </VLabel>
            <VSelect v-model="selectedAccount.locale_id" :items="locales" item-title="name" item-value="id" />
          </VCol>
          <VCol v-if="props.accountType == 'user'" cols="12" sm="8">
            <VLabel>
              {{ $t('tenants') }}
            </VLabel>
            <VSelect v-model="selectedAccount.tenant_ids" :items="tenants" item-title="name" item-value="id" multiple />
          </VCol>

          <VCol cols="6" sm="4">
            <VLabel>
              {{ $t('country') }}
            </VLabel>
            <VSelect v-model="selectedAccount.account.country_id" :items="countryList" item-title="name" item-value="id" />
          </VCol>
          <VCol cols="12" sm="8">
            <VLabel>
              {{ $t('address') }}
            </VLabel>
            <VTextField v-model="selectedAccount.account.address" name="address" variant="outlined" density="compact" />
          </VCol>
          <VCol v-if="props.accountType == 'admin'" cols="6" sm="4">
            <VLabel>
              {{ $t('role') }}
            </VLabel>
            <VSelect v-model="selectedAccount.role_id" :items="roles" :loading="rolesLoading" item-title="name" item-value="id" />
          </VCol>
        </VRow>
      </VForm>
    </template>
  </BaseDialog>
</template>
